/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { Container, ContainerProps } from 'src/core/components/Container';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { Table } from 'src/core/components/Table';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { getStylist } from 'src/core/themes/luisStylist';
import { SearchStrategy } from 'src/core/utils/searchStrategies';

export type ListModalProps<T> = BasicModalProps<ListModalDto<T>> & {
    /**
     * The key parameter to use to search.
     */
    searchKeyParam: keyof T;

    /**
     * A subtitle to display in the modal.
     */
    subtitle?: string;

    /**
     * A placeholder to use in the search box.
     */
    searchPlaceholder?: string;

    /**
     * The column header to display.
     */
    columnHeader?: string;

    /**
     * The items to list in the table.
     */
    items?: T[];

    /**
     * Whether items are loading or not.
     */
    isLoading?: boolean;

    /**
     * A custom render function to use to render the column.
     */
    onColumnRender?: (item?: T, index?: number, column?: IColumn) => JSX.Element;
};

export type ListModalDto<T> = {
    /**
     * The items that were selected.
     */
    selectedItems: T[];
};

const messages = defineMessages({
    defaultSearchPlaceholder: {
        id: 'core.listModal.defaultSearchPlaceholder',
        defaultMessage: 'Search items ...'
    },
    nameColumn: {
        id: 'core.listModal.nameColumn',
        defaultMessage: 'Name'
    },
    emptyPlaceholder: {
        id: 'core.listModal.emptyPlaceholder',
        defaultMessage: 'No items found'
    }
});

const { styleComponent } = getStylist('ListModal');
const TableContainer = styleComponent<ContainerProps>(Container)('TableContainer', theme => ({ maxHeight: theme.maxHeight }));
const NameStack = styleComponent<IStackProps>(Stack)('NameRow', theme => ({
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: theme.fontSize,
    color: theme.primaryColor,
    letterSpacing: theme.letterSpacing,
    fontWeight: theme.fontWeight as Property.FontWeight,
    $nest: { '&>small': { color: theme.secondaryFontColor, fontSize: theme.secondaryFontSize } }
}));

const NameRow = (props: IStackProps) => (
    <NameStack {...props} verticalFill horizontal verticalAlign="center">
        {props.children}
    </NameStack>
);

/**
 * Represents a modal component that lists given items in a table format, and
 * provides a search box to filter these items.
 *
 * @param props The modal props.
 */
export const ListModal = <T extends object>(props: ListModalProps<T>) => {
    const { localize } = useLocalization();
    const {
        subtitle,
        onSubmit,
        isLoading,
        items = [],
        columnHeader,
        searchKeyParam,
        searchPlaceholder = localize(messages.defaultSearchPlaceholder),
        onColumnRender = (item: T) => <NameRow>{item[searchKeyParam] as React.ReactNode}</NameRow>
    } = props;

    const [query, setQuery] = React.useState<string>('');
    const { selection, selectedItems } = useSelection<T>();

    const filteredItems = SearchStrategy.substringSearchStrategy(items, query, searchKeyParam);

    /**
     * Ensures that selection is cleared when filtered items length
     * is changed due to a change in the query.
     */
    React.useEffect(() => selection.setAllSelected(false), [filteredItems.length]);

    const column: IColumn[] = [
        {
            key: 'column1',
            name: columnHeader,
            fieldName: searchKeyParam,
            minWidth: 100,
            ariaLabel: columnHeader,
            onRender: onColumnRender
        }
    ];

    return (
        <BasicModal {...props} onSubmit={() => onSubmit({ selectedItems })} valid={selectedItems.length > 0}>
            <Stack tokens={{ childrenGap: 20 }}>
                <Text>{subtitle}</Text>

                <LuisDebouncedSearchBox
                    ariaLabel={searchPlaceholder}
                    autoFocus
                    value={query}
                    placeholder={searchPlaceholder}
                    onChange={(_, newValue?: string) => setQuery(newValue)}
                    debounceTime={0}
                />

                <TableContainer size="none">
                    <Table<T>
                        columns={column}
                        isLoading={isLoading}
                        items={filteredItems}
                        emptyPlaceholder={localize(messages.emptyPlaceholder)}
                        areItemsFiltered={Boolean(query)}
                        overallItemsCount={filteredItems.length}
                        selection={selection}
                        onShouldVirtualize={() => false}
                    />
                </TableContainer>
            </Stack>
        </BasicModal>
    );
};
